import type { Async, RealmApi, RealmMethod, SyncInfo, WorkerEvent, WorkerResponse } from './protocol.ts';
import { realmWorkerName } from './protocol.ts';

/** A failure reported by the realm worker, with its message written for the user. */
export class RealmClientError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'RealmClientError';
  }
}

/**
 * One realm worker per account (docs/ACCOUNTS-PLAN.md section 5). The worker learns whose
 * realms it serves from its name and keeps them in that account's own storage, so two
 * people sharing a browser never see each other's realms. It reads nothing until it has
 * the account's realms key (docs/REALM-ENCRYPTION.md). Signing out stops it.
 */
let active: { userId: string; worker: Worker } | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();

function failPending(error: Error): void {
  for (const call of pending.values()) call.reject(error);
  pending.clear();
}

const syncListeners = new Set<(file: string, info: SyncInfo) => void>();
const realmsListeners = new Set<() => void>();

/** Hears when the list of realms changed because of another device (a newer version taken, a realm deleted there). */
export function onRealmsChanged(listener: () => void): () => void {
  realmsListeners.add(listener);
  return () => realmsListeners.delete(listener);
}

/** Hears each realm's backup state as it changes (db/sync.ts). Returns the unsubscribe. */
export function onSyncChange(listener: (file: string, info: SyncInfo) => void): () => void {
  syncListeners.add(listener);
  return () => syncListeners.delete(listener);
}

// Uploads what's waiting when the page may be closing, and when the connection comes back.
if (typeof document !== 'undefined') {
  const flush = () => void (active ? call('syncNow', []).catch(() => undefined) : undefined);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flush());
  window.addEventListener('online', flush);
}

function start(userId: string): Worker {
  const worker = new Worker(new URL('./realm.worker.ts', import.meta.url), { type: 'module', name: realmWorkerName(userId) });
  worker.onmessage = ({ data }: MessageEvent<WorkerResponse | WorkerEvent>) => {
    if ('event' in data) {
      if (data.event === 'sync') for (const listener of syncListeners) listener(data.file, data.info);
      else for (const listener of realmsListeners) listener();
      return;
    }
    const call = pending.get(data.id);
    if (!call) return;
    pending.delete(data.id);
    if (data.ok) {
      call.resolve(data.value);
    } else {
      call.reject(new RealmClientError(data.error.message, data.error.code));
    }
  };
  worker.onerror = (event) => {
    failPending(new RealmClientError(`The realm database stopped: ${event.message || 'unknown error'}`));
  };
  return worker;
}

/** Opens (or switches to) this account's realms, unlocked with its realms key. */
export function openRealmsOf(userId: string, realmsKey: CryptoKey): void {
  if (active?.userId === userId) return;
  closeRealms();
  active = { userId, worker: start(userId) };
  // Sent first, so every later request finds the worker unlocked.
  void call('unlock', [realmsKey]).catch(() => undefined);
}

/** Closes the realms: after this, nothing can read them until an account opens them again. */
export function closeRealms(): void {
  if (!active) return;
  active.worker.terminate();
  active = null;
  failPending(new RealmClientError('You are signed out.', 'signed-out'));
}

function call<M extends RealmMethod>(method: M, args: Parameters<RealmApi[M]>): Promise<Awaited<ReturnType<RealmApi[M]>>> {
  if (!active) return Promise.reject(new RealmClientError('Sign in to open your realms.', 'signed-out'));
  const { worker } = active;
  const id = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
    worker.postMessage({ id, method, args });
  });
}

/** The page's handle on the realm database worker. */
export const realms: Async<RealmApi> = {
  unlock: (realmsKey) => call('unlock', [realmsKey]),
  listRealms: () => call('listRealms', []),
  createRealm: (name, description) => call('createRealm', [name, description]),
  importRealm: (bytes, onDuplicate) => call('importRealm', [bytes, onDuplicate]),
  exportRealm: (file) => call('exportRealm', [file]),
  deleteRealm: (file) => call('deleteRealm', [file]),
  openRealm: (file) => call('openRealm', [file]),
  getEntry: (file, entryId) => call('getEntry', [file, entryId]),
  createEntry: (file, entry) => call('createEntry', [file, entry]),
  updateEntry: (file, entryId, changes) => call('updateEntry', [file, entryId, changes]),
  deleteEntry: (file, entryId) => call('deleteEntry', [file, entryId]),
  deletionImpact: (file, entryId) => call('deletionImpact', [file, entryId]),
  lockEntry: (file, entryId, lock) => call('lockEntry', [file, entryId, lock]),
  removeLock: (file, entryId, lock) => call('removeLock', [file, entryId, lock]),
  editorSuggestions: (file) => call('editorSuggestions', [file]),
  addImage: (file, entryId, image, id, created) => call('addImage', [file, entryId, image, id, created]),
  removeImage: (file, imageId) => call('removeImage', [file, imageId]),
  createCollection: (file, name) => call('createCollection', [file, name]),
  setEntryCollections: (file, entryId, collectionIds) => call('setEntryCollections', [file, entryId, collectionIds]),
  setCollectionAudience: (file, collectionId, audience) => call('setCollectionAudience', [file, collectionId, audience]),
  setRealmAudience: (file, audience) => call('setRealmAudience', [file, audience]),
  exportData: (file) => call('exportData', [file]),
  buildEdition: (file, audience, fogged, key) => call('buildEdition', [file, audience, fogged, key]),
  openPreview: (file, audience, fogged) => call('openPreview', [file, audience, fogged]),
  coOwnedState: (file) => call('coOwnedState', [file]),
  setLease: (file, held) => call('setLease', [file, held]),
  catchUp: (file) => call('catchUp', [file]),
  sendNow: (file) => call('sendNow', [file]),
  realmKeyRaw: (file) => call('realmKeyRaw', [file]),
  rekeyRealm: (file) => call('rekeyRealm', [file]),
  storeShared: (copy) => call('storeShared', [copy]),
  sharedCopies: () => call('sharedCopies', []),
  forgetShared: (file) => call('forgetShared', [file]),
  listTables: (file) => call('listTables', [file]),
  getTable: (file, tableId) => call('getTable', [file, tableId]),
  createTable: (file, name, description) => call('createTable', [file, name, description]),
  updateTable: (file, tableId, name, description, entries) => call('updateTable', [file, tableId, name, description, entries]),
  importTables: (file, tables, target) => call('importTables', [file, tables, target]),
  deleteTable: (file, tableId) => call('deleteTable', [file, tableId]),
  chronology: (file, timelineId) => call('chronology', [file, timelineId]),
  storyProjects: (file) => call('storyProjects', [file]),
  storyProject: (file, projectId, sceneId) => call('storyProject', [file, projectId, sceneId]),
  createStoryProject: (file, project) => call('createStoryProject', [file, project]),
  updateStoryProject: (file, projectId, project) => call('updateStoryProject', [file, projectId, project]),
  storyDeletionImpact: (file, level, id) => call('storyDeletionImpact', [file, level, id]),
  deleteStoryItem: (file, level, id) => call('deleteStoryItem', [file, level, id]),
  createAct: (file, projectId, title, notes) => call('createAct', [file, projectId, title, notes]),
  updateAct: (file, actId, title, notes) => call('updateAct', [file, actId, title, notes]),
  createChapter: (file, actId, title, notes) => call('createChapter', [file, actId, title, notes]),
  updateChapter: (file, chapterId, title, notes) => call('updateChapter', [file, chapterId, title, notes]),
  createScene: (file, chapterId, title, wordGoal) => call('createScene', [file, chapterId, title, wordGoal]),
  updateSceneOutline: (file, sceneId, title, wordGoal) => call('updateSceneOutline', [file, sceneId, title, wordGoal]),
  moveStoryItem: (file, level, id, direction) => call('moveStoryItem', [file, level, id, direction]),
  updateScene: (file, sceneId, changes, keepRevisions) => call('updateScene', [file, sceneId, changes, keepRevisions]),
  sceneRevision: (file, revisionId) => call('sceneRevision', [file, revisionId]),
  restoreRevision: (file, sceneId, revisionId, keepRevisions) => call('restoreRevision', [file, sceneId, revisionId, keepRevisions]),
  recordWritingSession: (file, session) => call('recordWritingSession', [file, session]),
  ignoredMentions: (file, scope) => call('ignoredMentions', [file, scope]),
  ignoreMention: (file, scope, key, label) => call('ignoreMention', [file, scope, key, label]),
  stopIgnoringMention: (file, scope, key) => call('stopIgnoringMention', [file, scope, key]),
  campaigns: (file) => call('campaigns', [file]),
  campaign: (file, campaignId, withRun) => call('campaign', [file, campaignId, withRun]),
  setActiveEncounter: (file, campaignId, encounterId) => call('setActiveEncounter', [file, campaignId, encounterId]),
  runEncounter: (file, campaignId, encounterId, command, stamp) => call('runEncounter', [file, campaignId, encounterId, command, stamp]),
  stepSheetResource: (file, campaignId, sheetId, fieldKey, delta, stamp) => call('stepSheetResource', [file, campaignId, sheetId, fieldKey, delta, stamp]),
  setSheetConditions: (file, campaignId, sheetId, conditions, line, stamp) => call('setSheetConditions', [file, campaignId, sheetId, conditions, line, stamp]),
  appendPlayLog: (file, campaignId, line, stamp) => call('appendPlayLog', [file, campaignId, line, stamp]),
  maps: (file) => call('maps', [file]),
  atlasMap: (file, mapId) => call('atlasMap', [file, mapId]),
  importMap: (file, name, image, thumbnail, defaults) => call('importMap', [file, name, image, thumbnail, defaults]),
  updateMapSettings: (file, mapId, scale, grid) => call('updateMapSettings', [file, mapId, scale, grid]),
  updateRealmDetails: (file, name, description) => call('updateRealmDetails', [file, name, description]),
  proofingContext: (file, scopeId) => call('proofingContext', [file, scopeId]),
  addToRealmDictionary: (file, word) => call('addToRealmDictionary', [file, word]),
  removeFromRealmDictionary: (file, word) => call('removeFromRealmDictionary', [file, word]),
  ignoreSpelling: (file, scopeId, key) => call('ignoreSpelling', [file, scopeId, key]),
  icons: (file) => call('icons', [file]),
  importIcons: (file, icons) => call('importIcons', [file, icons]),
  renameIcon: (file, iconId, name) => call('renameIcon', [file, iconId, name]),
  iconUsage: (file, iconId) => call('iconUsage', [file, iconId]),
  deleteIcon: (file, iconId) => call('deleteIcon', [file, iconId]),
  updateFog: (file, mapId, enabled, revealed) => call('updateFog', [file, mapId, enabled, revealed]),
  createLayer: (file, mapId, name) => call('createLayer', [file, mapId, name]),
  updateLayer: (file, layerId, name, playersCanSee) => call('updateLayer', [file, layerId, name, playersCanSee]),
  moveLayer: (file, layerId, up) => call('moveLayer', [file, layerId, up]),
  deleteLayer: (file, layerId) => call('deleteLayer', [file, layerId]),
  saveDrawing: (file, mapId, drawing) => call('saveDrawing', [file, mapId, drawing]),
  deleteDrawing: (file, mapId, drawingId) => call('deleteDrawing', [file, mapId, drawingId]),
  restoreDrawings: (file, mapId, drawings) => call('restoreDrawings', [file, mapId, drawings]),
  renameMap: (file, mapId, name) => call('renameMap', [file, mapId, name]),
  storeMapThumbnail: (file, mapId, thumbnail) => call('storeMapThumbnail', [file, mapId, thumbnail]),
  mapDeletionImpact: (file, mapId) => call('mapDeletionImpact', [file, mapId]),
  deleteMap: (file, mapId) => call('deleteMap', [file, mapId]),
  createPin: (file, mapId, at, categoryId) => call('createPin', [file, mapId, at, categoryId]),
  movePin: (file, pinId, at) => call('movePin', [file, pinId, at]),
  updatePin: (file, pinId, details) => call('updatePin', [file, pinId, details]),
  deletePin: (file, pinId) => call('deletePin', [file, pinId]),
  createRegion: (file, mapId, points, categoryId) => call('createRegion', [file, mapId, points, categoryId]),
  updateRegion: (file, regionId, details) => call('updateRegion', [file, regionId, details]),
  deleteRegion: (file, regionId) => call('deleteRegion', [file, regionId]),
  createCategory: (file, details) => call('createCategory', [file, details]),
  updateCategory: (file, categoryId, details) => call('updateCategory', [file, categoryId, details]),
  categoryUse: (file, categoryId) => call('categoryUse', [file, categoryId]),
  deleteCategory: (file, categoryId) => call('deleteCategory', [file, categoryId]),
  boards: (file) => call('boards', [file]),
  board: (file, boardId) => call('board', [file, boardId]),
  createBoard: (file, details) => call('createBoard', [file, details]),
  updateBoard: (file, boardId, details) => call('updateBoard', [file, boardId, details]),
  deleteBoard: (file, boardId) => call('deleteBoard', [file, boardId]),
  createBoardObject: (file, boardId, object) => call('createBoardObject', [file, boardId, object]),
  createArrow: (file, boardId, fromId, toId) => call('createArrow', [file, boardId, fromId, toId]),
  moveBoardObject: (file, objectId, x, y) => call('moveBoardObject', [file, objectId, x, y]),
  updateBoardObject: (file, objectId, details) => call('updateBoardObject', [file, objectId, details]),
  bringToFront: (file, objectId) => call('bringToFront', [file, objectId]),
  deleteBoardObject: (file, objectId) => call('deleteBoardObject', [file, objectId]),
  createCampaign: (file, name, description) => call('createCampaign', [file, name, description]),
  updateCampaign: (file, campaignId, changes) => call('updateCampaign', [file, campaignId, changes]),
  deleteCampaign: (file, campaignId) => call('deleteCampaign', [file, campaignId]),
  createSession: (file, campaignId) => call('createSession', [file, campaignId]),
  updateSession: (file, sessionId, changes) => call('updateSession', [file, sessionId, changes]),
  deleteSession: (file, sessionId) => call('deleteSession', [file, sessionId]),
  setCurrentSession: (file, campaignId, sessionId) => call('setCurrentSession', [file, campaignId, sessionId]),
  createEncounter: (file, campaignId, details) => call('createEncounter', [file, campaignId, details]),
  updateEncounterDetails: (file, encounterId, details) => call('updateEncounterDetails', [file, encounterId, details]),
  deleteEncounter: (file, encounterId) => call('deleteEncounter', [file, encounterId]),
  createQuest: (file, campaignId) => call('createQuest', [file, campaignId]),
  updateQuest: (file, questId, changes) => call('updateQuest', [file, questId, changes]),
  deleteQuest: (file, questId) => call('deleteQuest', [file, questId]),
  knowledgeModes: (file) => call('knowledgeModes', [file]),
  consistency: (file) => call('consistency', [file]),
  dismissFinding: (file, finding, note) => call('dismissFinding', [file, finding, note]),
  restoreFinding: (file, fingerprint) => call('restoreFinding', [file, fingerprint]),
  calendars: (file) => call('calendars', [file]),
  calendar: (file, calendarId) => call('calendar', [file, calendarId]),
  createCalendar: (file, name, description) => call('createCalendar', [file, name, description]),
  updateCalendar: (file, calendarId, details) => call('updateCalendar', [file, calendarId, details]),
  deleteCalendar: (file, calendarId) => call('deleteCalendar', [file, calendarId]),
  setActiveCalendar: (file, calendarId) => call('setActiveCalendar', [file, calendarId]),
  eventsOnDay: (file, day) => call('eventsOnDay', [file, day]),
  eventsBetween: (file, from, to) => call('eventsBetween', [file, from, to]),
  lastEventDay: (file) => call('lastEventDay', [file]),
  createTimeline: (file, name, description, parentId) => call('createTimeline', [file, name, description, parentId]),
  updateTimeline: (file, timelineId, name, description) => call('updateTimeline', [file, timelineId, name, description]),
  timelineDeletionImpact: (file, timelineId) => call('timelineDeletionImpact', [file, timelineId]),
  deleteTimeline: (file, timelineId) => call('deleteTimeline', [file, timelineId]),
  createEra: (file, timelineId, era) => call('createEra', [file, timelineId, era]),
  updateEra: (file, eraId, era) => call('updateEra', [file, eraId, era]),
  deleteEra: (file, eraId) => call('deleteEra', [file, eraId]),
  createEvent: (file, timelineId, event) => call('createEvent', [file, timelineId, event]),
  updateEvent: (file, eventId, event) => call('updateEvent', [file, eventId, event]),
  eventSceneLinks: (file, eventId) => call('eventSceneLinks', [file, eventId]),
  deleteEvent: (file, eventId) => call('deleteEvent', [file, eventId]),
  sheetsOverview: (file) => call('sheetsOverview', [file]),
  getSheet: (file, sheetId) => call('getSheet', [file, sheetId]),
  entrySheet: (file, entryId) => call('entrySheet', [file, entryId]),
  templateChoices: (file) => call('templateChoices', [file]),
  sheetEntryChoices: (file) => call('sheetEntryChoices', [file]),
  createSheet: (file, template, name, entryId) => call('createSheet', [file, template, name, entryId]),
  setSheetValues: (file, sheetId, values) => call('setSheetValues', [file, sheetId, values]),
  renameSheet: (file, sheetId, name) => call('renameSheet', [file, sheetId, name]),
  linkSheet: (file, sheetId, entryId) => call('linkSheet', [file, sheetId, entryId]),
  deleteSheet: (file, sheetId) => call('deleteSheet', [file, sheetId]),
  importSheet: (file, data) => call('importSheet', [file, data]),
  getTemplate: (file, templateId) => call('getTemplate', [file, templateId]),
  createTemplate: (file, name) => call('createTemplate', [file, name]),
  duplicateTemplate: (file, templateId) => call('duplicateTemplate', [file, templateId]),
  templateFromBuiltIn: (file, builtinKey) => call('templateFromBuiltIn', [file, builtinKey]),
  updateTemplate: (file, templateId, edit, renames) => call('updateTemplate', [file, templateId, edit, renames]),
  deleteTemplate: (file, templateId) => call('deleteTemplate', [file, templateId]),
  importTemplate: (file, data) => call('importTemplate', [file, data]),
  scriptsOverview: (file) => call('scriptsOverview', [file]),
  getScript: (file, scriptId) => call('getScript', [file, scriptId]),
  createScript: (file, name, description, source) => call('createScript', [file, name, description, source]),
  updateScript: (file, scriptId, name, description, source) => call('updateScript', [file, scriptId, name, description, source]),
  deleteScript: (file, scriptId) => call('deleteScript', [file, scriptId]),
  importScriptPack: (file, pack) => call('importScriptPack', [file, pack]),
  createGeneratedEntries: (file, entries) => call('createGeneratedEntries', [file, entries]),
  renameCollection: (file, collectionId, name) => call('renameCollection', [file, collectionId, name]),
  deleteCollection: (file, collectionId) => call('deleteCollection', [file, collectionId]),
  search: (file, query) => call('search', [file, query]),
  replace: (file, query, replacement, choices, options) => call('replace', [file, query, replacement, choices, options]),
  undoReplace: (file) => call('undoReplace', [file]),
  graph: (file) => call('graph', [file]),
  syncStates: () => call('syncStates', []),
  syncNow: () => call('syncNow', []),
  setBackup: (file, on) => call('setBackup', [file, on]),
  dismissCopyNote: (file) => call('dismissCopyNote', [file]),
  realmVersions: (file) => call('realmVersions', [file]),
  restoreVersion: (file, version) => call('restoreVersion', [file, version]),
  deletedRealms: () => call('deletedRealms', []),
  restoreRealm: (file) => call('restoreRealm', [file]),
  backupUsage: () => call('backupUsage', []),
};
